/**
 * A buyer's statement.
 *
 * **The single most-asked question in this business is "what have I paid and what is left".** So
 * this is a page somebody can be sent rather than a report staff build, and it prints, because
 * half of them will want it on paper.
 *
 * The same numbers as the purchase screen, arranged for the buyer rather than for the developer:
 * no controls, no state anybody but them cares about, and the balance where the eye lands. The
 * `printable` class is what puts it alone on the sheet — the rail, the bar and the buttons carry
 * `no-print` and are gone.
 */

import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Badge, Button, Card, ErrorState, PageHeader, Spinner, Table } from "@/components/ui";
import { useFormat } from "@/lib/format";
import { Copyright } from "@/components/copyright";
import { AgencyMark } from "@/components/agency-mark";

interface Statement {
  purchase: {
    reference: string;
    status: string;
    startDate: string;
    frequency: string;
    handedOverOn: string | null;
    allocationMethod: string | null;
  };
  buyer: { name: string; phone: string | null; email: string | null };
  estate: { name: string; city: string | null };
  plot: { label: string; sizeSqm: number | null } | null;
  instalments: Array<{
    id: string;
    label: string;
    dueDate: string;
    amountLabel: string;
    paidLabel: string;
    status: string;
  }>;
  standing: {
    priceLabel: string;
    billedLabel: string;
    paidLabel: string;
    outstandingLabel: string;
    arrearsLabel: string;
    creditLabel: string;
    arrears: number;
    credit: number;
    percentPaid: number;
    settled: boolean;
    nextDue: { dueDate: string; amount: number } | null;
  };
  /* `logoUrl` comes down with the rest of the agency block from `/purchases/:id/statement`,
     so this page needs no session to draw its own letterhead. */
  agency: { name: string; phone: string | null; email: string | null; address: string | null; logoUrl: string | null };
  /*
    Where what was bought is not what was bought at the start.

    Almost always empty. When it is not, this is the paragraph that makes the statement honest: the
    reference, the price and the schedule are all unchanged by a move — deliberately, because a
    move never re-prices an agreement — so without it the document names something the buyer never
    agreed to buy and says nothing about how they came to hold it.
  */
  moves: Array<{
    id: string;
    movedOn: string;
    cancelled: boolean;
    reason: string | null;
    fromLabel: string | null;
    toLabel: string | null;
  }>;
  /*
    Where this sale came out of a reservation.

    Null on a walk-in sale, which is most of them. Where it is not null the statement has to lead
    with it, because the fee counts toward the price rather than sitting on top of it — the buyer
    has already paid part of what the schedule below asks for, and a statement that does not say so
    reads as a demand for the same money twice.
  */
  reservation: {
    id: string;
    reference: string;
    reservedOn: string;
    feeAmountLabel: string;
    paid: number;
    paidLabel: string;
    refundable: boolean;
  } | null;
  /*
    Every receipt, so the fee can be pointed at.

    The plan above shows what each instalment has received; this shows what actually arrived and
    when. The holding fee is **first** here because it genuinely was the first money — it is
    allocated to the oldest instalment at the moment of conversion — and nothing sorts it there.
  */
  receipts: Array<{
    id: string;
    receiptNumber: string;
    amount: number;
    method: string;
    receivedOn: string;
    status: string;
    reservationId: string | null;
  }>;
  asAt: string;
}

export function PurchaseStatementPage() {
  const { id = "" } = useParams();
  const fmt = useFormat();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["purchase-statement", id],
    queryFn: () => api.get<Statement>(`/purchases/${id}/statement`),
  });

  if (isLoading) return <Spinner />;
  if (error || !data) return <ErrorState error={error} retry={() => refetch()} />;

  const { standing, buyer, estate, plot, purchase, agency, moves } = data;

  return (
    <div className="max-w-3xl">
      <PageHeader
        title="Statement"
        back={{ to: `/app/development/sales/${id}`, label: "Sale" }}
        description="A statement of amounts received and the balance outstanding. It can be sent or printed."
        actions={
          <Button variant="secondary" className="no-print" onClick={() => window.print()}>
            Print
          </Button>
        }
      />

      <Card padded={false} className="printable">
        <div className="border-b border-ink-200 p-6">
          <div className="flex flex-wrap items-start gap-4">
            {/* The letterhead a buyer is sent. Nothing changes for a developer who has not
                uploaded a mark. */}
            <AgencyMark name={agency.name} logoUrl={agency.logoUrl}>
              {agency.address ? <p className="text-sm text-ink-500">{agency.address}</p> : null}
              <p className="text-sm text-ink-500">
                {[agency.phone, agency.email].filter(Boolean).join(" · ")}
              </p>
            </AgencyMark>
            <div className="ml-auto text-right">
              <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Statement</p>
              <p className="font-medium">{purchase.reference}</p>
              <p className="text-sm text-ink-500">as at {data.asAt}</p>
            </div>
          </div>
        </div>

        <div className="grid gap-6 border-b border-ink-200 p-6 sm:grid-cols-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Buyer</p>
            <p className="mt-1 font-medium">{buyer.name}</p>
            <p className="text-sm text-ink-500">{[buyer.phone, buyer.email].filter(Boolean).join(" · ")}</p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Property purchased</p>
            <p className="mt-1 font-medium">
              {plot ? `${plot.label}, ${estate.name}` : estate.name}
            </p>
            <p className="text-sm text-ink-500">
              {plot?.sizeSqm ? `${plot.sizeSqm} m² · ` : ""}
              {/* Which one they have is a later decision, and the statement says so rather than
                  naming a thing that is not theirs yet. */}
              {plot ? "" : "allocation not yet decided · "}
              agreed {purchase.startDate}
            </p>
          </div>
        </div>

        {/* Above the money rather than below it: somebody reading a figure has to know first what
            the figure is against. Dated, because "when" is half the question. */}
        {moves.length ? (
          <div className="border-b border-ink-200 p-6" data-moves>
            <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">What was bought has changed</p>
            <ul className="mt-2 space-y-1 text-sm">
              {moves.map((move) => (
                <li key={move.id}>
                  <span className="font-medium">{move.movedOn}</span> —{" "}
                  {move.cancelled
                    ? `${move.fromLabel ?? "what was bought"} was withdrawn and this sale was cancelled`
                    : `${move.fromLabel ?? "what was bought"} became ${move.toLabel ?? "another"}`}
                  {move.reason ? `. ${move.reason}` : "."}
                </li>
              ))}
            </ul>
            <p className="mt-2 text-sm text-ink-500">
              The price below is the price agreed at the outset and is unchanged by this.
            </p>
          </div>
        ) : null}

        {/* Above the money, for the same reason a move is: somebody about to read a figure has to
            know first what has already been paid against it. */}
        {data.reservation ? (
          <div className="border-b border-ink-200 p-6" data-reservation={data.reservation.reference}>
            <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Reserved first</p>
            <p className="mt-2 text-sm">
              Reserved on <span className="font-medium">{data.reservation.reservedOn}</span> under{" "}
              <span className="font-medium">{data.reservation.reference}</span>.
              {data.reservation.paid > 0
                ? ` A holding fee of ${data.reservation.paidLabel} was received and counts toward the price below — it is not asked for again.`
                : " No holding fee was received against it, so the whole price is on the schedule below."}
            </p>
          </div>
        ) : null}

        {/* The balance, where the eye lands. It is the whole reason anybody opens this. */}
        <div className="grid gap-4 border-b border-ink-200 p-6 sm:grid-cols-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Price</p>
            <p className="mt-1 text-xl font-semibold">{standing.priceLabel}</p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Received</p>
            <p className="mt-1 text-xl font-semibold">{standing.paidLabel}</p>
            <p className="text-xs text-ink-500">{standing.percentPaid}%</p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Outstanding</p>
            <p className="mt-1 text-xl font-semibold">{standing.outstandingLabel}</p>
            {standing.credit > 0 ? <p className="text-xs text-success-700">{standing.creditLabel} in credit</p> : null}
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">
              {standing.arrears > 0 ? "Overdue" : "Next payment"}
            </p>
            <p className={`mt-1 text-xl font-semibold ${standing.arrears > 0 ? "text-danger-600" : ""}`}>
              {standing.arrears > 0 ? standing.arrearsLabel : standing.nextDue ? standing.nextDue.dueDate : "—"}
            </p>
          </div>
        </div>

        <Table
          head={
            <tr>
              <th>Payment</th>
              <th>Due</th>
              <th className="num">Amount</th>
              <th className="num">Received</th>
              <th>Status</th>
            </tr>
          }
        >
          {data.instalments.map((row) => (
            <tr key={row.id}>
              <td className="font-medium">{row.label}</td>
              <td className="text-ink-600">{row.dueDate}</td>
              <td className="num">{row.amountLabel}</td>
              <td className="num text-ink-600">{row.paidLabel}</td>
              <td className="text-ink-600">{row.status.replace("_", " ")}</td>
            </tr>
          ))}
        </Table>

        {/*
          What actually arrived, oldest first.

          The plan above is what was agreed; this is what has been paid against it, and the two
          answer different questions. The holding fee is named rather than left as an ordinary
          receipt on a date before the plan started, which is what it would otherwise look like.
        */}
        {data.receipts.length ? (
          <div className="border-t border-ink-200 p-6">
            <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Amounts received</p>
            <ul className="mt-2 space-y-1.5 text-sm" data-receipts>
              {[...data.receipts]
                .sort((a, b) => (a.receivedOn < b.receivedOn ? -1 : a.receivedOn > b.receivedOn ? 1 : 0))
                .map((receipt) => (
                  <li key={receipt.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-ink-100 py-1.5 last:border-0">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{receipt.receivedOn}</span>
                      <span className="text-ink-500">{receipt.receiptNumber}</span>
                      {receipt.reservationId ? <Badge tone="brand">Holding fee</Badge> : null}
                      {receipt.status === "reversed" ? <Badge tone="danger">Reversed</Badge> : null}
                    </span>
                    <span className="num font-medium">{fmt.money(receipt.amount)}</span>
                  </li>
                ))}
            </ul>
          </div>
        ) : null}

        <div className="border-t border-ink-200 p-6 text-xs text-ink-500">
          <p>
            {standing.settled
              ? "This payment plan has been settled in full."
              : `Please quote ${purchase.reference} on any payment so it reaches the right account.`}
          </p>
          {purchase.handedOverOn ? <p className="mt-1">Handed over on {purchase.handedOverOn}.</p> : null}
          <Copyright className="mt-3" />
        </div>
      </Card>
    </div>
  );
}
